'use client'
// NOTE: price display in product detail page

import { Skeleton } from '@/components/ui/skeleton'
import { TagThumbnail } from '@/components/ui/tag-thumbnail'
import { useCampaignPrice } from '@/hooks/common/use-campaign-price'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { formatPrice } from '@/lib/utils'
import { ProductDetailFormatted } from '@/types/product-detail'
import Image from 'next/image'

interface Props {
  product: ProductDetailFormatted
}

/**
 * Hiển thị thông tin sản phẩm ngắn gọn, ưu tiên giá campaign nếu đang active.
 * Nếu campaign đang active, lấy giá campaign (finalPrice), giá gốc (originalPrice), phần trăm giảm (discountPercent).
 * Nếu đang loading campaign, hiển thị skeleton.
 */
export const InfoProductShort = ({ product }: Props) => {
  // Lấy giá campaign nếu có, truyền regularPrice và comparePrice từ product
  const {
    finalPrice: campaignPrice,
    originalPrice: campaignOriginalPrice,
    discountPercent: campaignDiscountPercent,
    shouldShowDiscount: campaignShouldShowDiscount,
    isLoading: isCampaignLoading,
  } = useCampaignPrice({
    regularPrice: product.regular_price ?? 0,
    comparePrice: product.compare_price ?? 0,
    product,
  })

  // Nếu campaign đang active thì lấy giá campaign, ngược lại lấy giá thường
  const displayPrice = campaignShouldShowDiscount
    ? campaignPrice
    : product.regular_price
  // Giá gạch ngang: nếu campaign active và giá gốc khác giá campaign thì lấy giá gốc, ngược lại lấy comparePrice
  const displayComparePrice =
    campaignShouldShowDiscount && campaignOriginalPrice !== campaignPrice
      ? campaignOriginalPrice
      : product.compare_price
  // Phần trăm giảm: nếu campaign active và có giảm giá thì lấy campaignDiscountPercent, ngược lại lấy salePercent truyền vào
  const displayDiscountPercent =
    campaignShouldShowDiscount && campaignDiscountPercent > 0
      ? campaignDiscountPercent
      : product.sale_percent

  if (!product) return null

  return (
    <div className='flex flex-1 gap-2 py-2'>
      {product.default_thumbnail ? (
        isCampaignLoading ? (
          <Skeleton className='h-[99px] max-h-24 w-[74px] rounded-lg' />
        ) : (
          <Image
            src={ImageHelper.getImageUrl(product.default_thumbnail, '', 148)}
            alt='Product Image'
            width={74}
            height={99}
            className='max-h-24 rounded-lg object-cover'
          />
        )
      ) : null}
      <div className='min-w-0 flex-1 py-2'>
        <p className='line-clamp-1 font-medium'>{product.display_name}</p>
        <div>
          {/* Hiển thị skeleton khi đang loading campaign price */}
          {isCampaignLoading ? (
            <div className='flex flex-col gap-1'>
              <Skeleton className='mb-1 h-4 w-20' />
              <div className='flex items-center gap-2'>
                <Skeleton className='h-7 w-24 rounded' />
                <Skeleton className='h-5 w-10 rounded' />
              </div>
            </div>
          ) : (
            <>
              {/* Hiển thị giá gạch ngang nếu có giảm giá */}
              {displayComparePrice && displayComparePrice !== displayPrice ? (
                <del className='mb-1 font-sans text-sm leading-4.5 text-neutral-800/30'>
                  {formatPrice(displayComparePrice)}
                </del>
              ) : null}
              <p className='flex items-center font-sans text-lg font-bold leading-6 text-neutral-800'>
                {formatPrice(displayPrice)}{' '}
                {displayDiscountPercent && displayDiscountPercent > 0 ? (
                  <TagThumbnail
                    title={`${displayDiscountPercent}%`}
                    className='ml-1 h-6 rounded-md'
                    variant='neutral'
                    displayDiscountPercent={displayDiscountPercent}
                  />
                ) : null}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
